import React, { useRef, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import LightGallery from 'lightgallery/react';
import lightGallery from 'lightgallery';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { Attachment } from '../../models/Attachment.interface';
import './AttachmentGalleryModal.css';

interface AttachmentGalleryModalProps {
    isOpen: boolean;
    toggle: () => void;
    attachments: Attachment[];
    initialIndex?: number;
}

const AttachmentGalleryModal: React.FC<AttachmentGalleryModalProps> = ({
    isOpen,
    toggle,
    attachments,
    initialIndex = 0
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lightGalleryRef = useRef<any>(null);
    const [lgOpened, setLgOpened] = useState(false);
    const dynamicGalleryRef = useRef<HTMLDivElement | null>(null);

    const imageAttachments = attachments.filter(attachment =>
        /\.(jpg|jpeg|png|gif|heic)/i.test(attachment.url)
    );

    // Clean up LightGallery instance
    const cleanupLightGallery = () => {
        if (lightGalleryRef.current) {
            try {
                lightGalleryRef.current.destroy();
            } catch (error) {
                console.error('Error destroying LightGallery:', error);
            }
            lightGalleryRef.current = null;
        }
        if (dynamicGalleryRef.current && dynamicGalleryRef.current.parentElement) {
            dynamicGalleryRef.current.parentElement.removeChild(dynamicGalleryRef.current);
        }
        dynamicGalleryRef.current = null;
        document.body.classList.remove('lg-show');
        setLgOpened(false);
    };

    // Cleanup on unmount or modal close
    useEffect(() => {
        return cleanupLightGallery;
    }, []);

    const handleImageClick = (index: number) => {
        // First, ensure any existing LightGallery instance is cleaned up
        cleanupLightGallery();

        // Create a new container for LightGallery
        const dynamicGallery = document.createElement('div');
        dynamicGallery.classList.add('lg-container');
        document.body.appendChild(dynamicGallery);
        dynamicGalleryRef.current = dynamicGallery;

        // Prepare gallery items
        const items = imageAttachments.map(attachment => ({
            src: attachment.url,
            thumb: attachment.url,
            subHtml: `<div class="lightbox-caption">${attachment.caption || ''}</div>`
        }));

        try {
            // Initialize LightGallery
            const lgInstance = lightGallery(dynamicGallery, {
                dynamic: true,
                dynamicEl: items,
                index: index,
                download: false,
                plugins: [lgThumbnail, lgZoom],
                speed: 500,
                mode: 'lg-slide', // Ensure sliding between images
                hideScrollbar: true,
                closable: true,
                escKey: true,
                mousewheel: false,
                appendSubHtmlTo: '.lg-sub-html',
                mobileSettings: {
                    controls: true,
                    showCloseIcon: true,
                    download: false
                }
            });

            // Store reference to the LightGallery instance
            lightGalleryRef.current = lgInstance;

            // Open the gallery
            lgInstance.openGallery(index);
            setLgOpened(true);

            // Add event listener for gallery close
            dynamicGallery.addEventListener('lgAfterClose', () => {
                cleanupLightGallery();
            });

        } catch (error) {
            console.error('Error initializing LightGallery:', error);
            cleanupLightGallery();
        }
    };

    const renderImageGrid = () => {
        const rows = [];
        for (let i = 0; i < imageAttachments.length; i += 3) {
            const rowImages = imageAttachments.slice(i, i + 3);
            if (rowImages.length === 0) break;

            const isEvenRow = Math.floor(i / 3) % 2 === 1;
            const row = (
                <div key={i} className={`grid-row ${isEvenRow ? 'grid-row-right' : 'grid-row-left'}`}>
                    {rowImages.map((attachment, index) => {
                        const isEvenRow = Math.floor(i / 3) % 2 === 1;
                        const gridArea = isEvenRow
                            ? (index === 2 ? 'featured' : index === 0 ? 'left-top' : 'left-bottom')
                            : (index === 0 ? 'featured' : index === 1 ? 'right-top' : 'right-bottom');

                        return (
                            <div
                                key={attachment.id || index}
                                className="grid-item"
                                style={{ gridArea }}
                                onClick={() => handleImageClick(i + index)}
                            >
                                <div className="gallery-item">
                                    <img
                                        alt={attachment.caption || `Image ${i + index + 1}`}
                                        src={attachment.url}
                                        loading={i < 6 ? 'eager' : 'lazy'}
                                    />
                                    {attachment.caption && (
                                        <div className="image-caption">
                                            {attachment.caption}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
            rows.push(row);
        }
        return rows;
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            className={`gallery-modal ${lgOpened ? 'lg-show' : ''}`}
            style={{
                maxWidth: '96%',
                margin: '2rem auto',
                height: '95%',
                width: 'min(1750px, calc(100vw - 4rem))',
                maxHeight: 'calc(100vh - 4rem)'
            }}
        >
            <ModalHeader toggle={toggle} className="bp-DialogHeader">
                <h5 className="modal-title">Image Gallery</h5>
            </ModalHeader>
            <ModalBody className="bp-dialog-content">
                <div className="bp-PhotoArea" ref={containerRef}>
                    <div className="bp-PhotoArea__grid">
                        {renderImageGrid()}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default AttachmentGalleryModal;